import { homerunnerApiBaseUrl } from '../../hr'
import { okOrThrow } from '../../common/http'
import { useInfiniteQuery } from '@tanstack/react-query'
import { z } from 'zod'

// #839: `has_response` used to be `z.boolean().default(false)`, and the false
// half was dead — `fetchReviews` only sent the param when true, so a stored
// `false` (the #838 default, present on 14/14 live reviews widgets as of
// 8/26) meant "no filter" and "only reviews without a response" was
// unreachable. The field is now a tri-state enum. The legacy boolean maps
// onto it so no saved widget changes behavior: false → 'any' (no filter, what
// it always did), true → 'with'. Central's public reviews endpoint honors
// `has_response=false` (ReviewController::applyFilters:
// `whereNotNull('response', 'and', !$hasResponse)`), so 'without' can send it.
//
// Also absorbs the dashboard-save artifacts (`null` from Laravel's
// ConvertEmptyStringsToNull, '' from older blobs) and the PHP string
// round-trip ("1"/"0") that `coerceConfigScalars` no longer sees once the
// leaf is not a ZodBoolean. The string vocabulary is exactly the one
// `coerceBool` in src/lib/widget-config-coerce.ts applied to this leaf while
// it was a boolean, so the dashboard-side reading is unchanged. Everything
// else — enum literals in any other spelling, other numbers, objects — is
// returned UNCHANGED so the enum rejects it loudly instead of silently
// coercing (only exact 'any' | 'with' | 'without' parse).
export const HAS_RESPONSE_OPTIONS = ['any', 'with', 'without'] as const
export type HasResponseFilter = (typeof HAS_RESPONSE_OPTIONS)[number]
export const HAS_RESPONSE_DESCRIPTION = 'Filter by owner response: any, with (responded), without (no response yet)'

const LEGACY_TRUE_STRINGS = new Set(['1', 'true', 'yes', 'on'])
const LEGACY_FALSE_STRINGS = new Set(['0', 'false', 'no', 'off', ''])

export function normalizeHasResponse(value: unknown): unknown {
  if (value === true || value === 1) return 'with'
  if (value === false || value === null || value === 0) return 'any'
  if (typeof value === 'string') {
    const s = value.trim().toLowerCase()
    if (LEGACY_TRUE_STRINGS.has(s)) return 'with'
    if (LEGACY_FALSE_STRINGS.has(s)) return 'any'
  }
  return value
}

export const reviewParamsSchema = z.object({
  feed_id: z.number().int().optional().describe('Filter reviews by specific feed'),
  property: z.string().optional().describe('Filter reviews by specific property slug'),
  platform: z.string().nullish().describe('Filter reviews by platform UUID'),
  per_page: z.number().min(1).max(20).default(10).describe('Number of reviews per page'),
  page: z.number().min(1).default(1).describe('Current page number'),
  rating: z.string().optional().describe('Filter by specific rating(s), comma-separated'),
  min_rating: z.number().min(1).max(5).optional().describe('Minimum rating filter'),
  max_rating: z.number().min(1).max(5).optional().describe('Maximum rating filter'),
  source: z.string().optional().describe('Filter by review source, comma-separated'),
  channel: z.string().optional().describe('Filter by review channel, comma-separated'),
  author: z.string().optional().describe('Search reviews by author name'),
  date_from: z.string().optional().describe('Filter reviews from date (YYYY-MM-DD)'),
  date_to: z.string().optional().describe('Filter reviews until date (YYYY-MM-DD)'),
  // Described on the inner enum AND the outer effects: the admin form engine
  // reaches the leaf through mergeZodOuterInnerType, whose merge lets a
  // wrapper's undefined description overwrite the outer one, while schema
  // introspection (docs, sentinels) reads `.description` on the outer.
  has_response: z.preprocess(
    normalizeHasResponse,
    z.enum(HAS_RESPONSE_OPTIONS).describe(HAS_RESPONSE_DESCRIPTION).default('any'),
  ).describe(HAS_RESPONSE_DESCRIPTION),
  search: z.string().optional().describe('Search in review title and content'),
  order_by: z.enum([
    'rating_asc', 'rating_desc',
    'date_asc', 'date_desc',
    'created_asc', 'created_desc',
    'author_asc', 'author_desc',
    'property_name_asc', 'property_name_desc',
    'source_asc', 'source_desc',
    'channel_asc', 'channel_desc',
  ]).default('date_desc').describe('Sort results'),
}).describe('Data filter options for reviews')

export type Review = {
  id: number
  property_id: number
  property_uuid: string
  property_name: string
  /**
   * The feed's slug for the review's property (Central #3704), the same value
   * /feed/properties serves; null when the feed has none. Absent on Centrals
   * older than #3704 — treat absent and null alike (no link).
   */
  property_slug?: string | null
  title: string | null
  content: string
  response: string | null
  rating: number
  date: string
  author: string | null
  source: string
  channel: string
  guests_count: number
  created_at: string
  updated_at: string
}

export type ReviewFilter = z.infer<typeof reviewParamsSchema>

export async function fetchReviews(filter: ReviewFilter) {
  const url = new URL(`${homerunnerApiBaseUrl()}/public-api/v1/reviews`)
  const { has_response, ...restFilter } = filter

  Object.entries(restFilter).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.append(key, String(value))
    }
  })

  // Tri-state on the wire (#839): 'with' → true, 'without' → false, 'any' →
  // param absent. Callers pass a parsed filter, but the legacy mapping is
  // re-applied so an unparsed boolean can never regress to the dead half.
  const responseMode = normalizeHasResponse(has_response)
  if (responseMode === 'with') {
    url.searchParams.append('has_response', 'true')
  } else if (responseMode === 'without') {
    url.searchParams.append('has_response', 'false')
  }
  const res = await fetch(url.toString())
  await okOrThrow(res, 'Failed to fetch reviews')
  const data = await res.json()

  return {
    ...data,
  } as {
    // Errors never reach this cast (guarded by res.ok above); canonical error
    // bodies carry `success: false` instead of `status` (central #2756).
    status: 'ok'
    result: Review[]
    pagination: {
      has_next_page: boolean
      has_prev_page: boolean
      page: number
      page_size: number
      total_items: number
      total_pages: number
      average_rating: number
    }
    status_code: number
    avgRating: number
  }
}

export function useFetchReviews(filter: ReviewFilter) {
  return useInfiniteQuery({
    queryKey: ['reviews', filter],
    queryFn: ({ pageParam = 1 }) => fetchReviews({ ...filter, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage?.pagination?.has_next_page) {
        return lastPage.pagination.page + 1
      }
      return undefined
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    staleTime: 0,
  })
}
